import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Trash2,
  Briefcase,
  Ticket as TicketIcon,
  Paperclip,
  X,
  AtSign,
  CheckCircle2,
  History,
  RefreshCw,
} from "lucide-react";
import { todayPanelTz, type SessionUser } from "@axorial/shared";
import type { Control } from "react-hook-form";
import {
  STANDUP_SECTION_LABELS,
  STANDUP_ITEM_SECTION_KEYS,
  standupCreateSchema,
  type StandupCreateInput,
  type PlaneIssueSummary,
  type Project,
  type User,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api, ApiError } from "@/lib/api";

export const Route = createFileRoute("/standups/new")({
  component: NewStandupPage,
  validateSearch: z.object({ edit: z.string().optional() }),
});

const todayIso = () => todayPanelTz();

/**
 * Layout final del formulario (orden y tipo de input por pregunta):
 *
 *   1. yesterday              — items (mapeo por tarea)
 *   2. yesterday_blockers     — texto libre
 *   3. general_difficulties   — texto libre
 *   4. today                  — texto libre
 *   5. today_blockers         — items (mapeo por tarea)
 *   6. tooling_friction       — texto libre
 *   7. comments               — texto libre
 */
const FORM_SECTIONS: Array<
  | { kind: "items"; key: (typeof STANDUP_ITEM_SECTION_KEYS)[number]; label: string }
  | {
      kind: "text";
      key:
        | "yesterdayBlockers"
        | "generalDifficulties"
        | "todayBlockers"
        | "toolingFriction"
        | "comments";
      label: string;
      placeholder?: string;
    }
> = [
  { kind: "items", key: "yesterday", label: STANDUP_SECTION_LABELS.yesterday },
  {
    kind: "text",
    key: "yesterdayBlockers",
    label: STANDUP_SECTION_LABELS.yesterday_blockers,
    placeholder: "N/A",
  },
  {
    kind: "text",
    key: "generalDifficulties",
    label: STANDUP_SECTION_LABELS.general_difficulties,
    placeholder: "N/A",
  },
  { kind: "items", key: "today", label: STANDUP_SECTION_LABELS.today },
  {
    kind: "text",
    key: "todayBlockers",
    label: STANDUP_SECTION_LABELS.today_blockers,
    placeholder: "N/A",
  },
  {
    kind: "text",
    key: "toolingFriction",
    label: STANDUP_SECTION_LABELS.tooling_friction,
    placeholder: "N/A",
  },
  {
    kind: "text",
    key: "comments",
    label: STANDUP_SECTION_LABELS.comments,
    placeholder: "Cualquier cosa relevante",
  },
];

function NewStandupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { edit: editingId } = Route.useSearch();
  const isEdit = !!editingId;

  const issuesQuery = useQuery<{ issues: PlaneIssueSummary[]; note?: string }>({
    queryKey: ["standups", "me", "issues"],
    queryFn: () => api.get("/standups/me/issues"),
  });
  const refreshIssues = useMutation({
    mutationFn: () =>
      api.get<{ issues: PlaneIssueSummary[]; note?: string }>("/standups/me/issues?refresh=1"),
    onSuccess: (fresh) => qc.setQueryData(["standups", "me", "issues"], fresh),
  });
  const projectsQuery = useQuery<{ projects: Project[] }>({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects"),
  });
  const usersQuery = useQuery<{ users: User[] }>({
    queryKey: ["admin", "users"],
    queryFn: () => api.get("/users"),
  });
  const meQuery = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });

  // En edit-mode cargamos el standup existente para prellenar el form.
  // Los items vienen con su `id` server-side preservado — el PATCH del
  // backend lo usa para hacer diff (update vs delete vs create).
  const existingQuery = useQuery({
    queryKey: ["standup", editingId],
    queryFn: () =>
      api.get<{
        standup: {
          date: string;
          yesterdayBlockers: string | null;
          generalDifficulties: string | null;
          todayBlockers: string | null;
          toolingFriction: string | null;
          comments: string | null;
          mentionedUserIds: string[];
        };
        items: Array<{
          id: string;
          section: "yesterday" | "today";
          position: number;
          text: string | null;
          panelProjectId: string | null;
        }>;
        tickets: Array<{
          id: string;
          itemId: string;
          position: number;
          planeProjectId: string;
          planeIssueId: string;
          issueTitleSnapshot: string;
          issueUrl: string;
          sequenceId: number | null;
          projectIdentifier: string | null;
        }>;
        mentions: Array<{ itemId: string; userId: string }>;
        attachments: Array<{
          id: string;
          section: string | null;
          itemId: string | null;
          filename: string;
          mimeType: string;
          sizeBytes: number;
          createdAt: number;
        }>;
      }>(`/standups/${editingId}`),
    enabled: isEdit,
  });

  // DELETE de un attachment existente. Invalida el query así el slot
  // se re-renderiza sin la imagen.
  const deleteAttachment = useMutation({
    mutationFn: (attId: string) => api.del(`/standups/attachments/${attId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["standup", editingId] });
    },
  });

  // Maps de attachments existentes por slot. La key matchea exactamente
  // los slotKeys que ya usamos para los pendientes:
  //   - "section:<section>" para attachments de pregunta libre
  //   - "item:<server-item-id>" para attachments de item
  // Nota: ItemCard en edit-mode usa el server id del item para construir
  // su slotKey, no el RHF field id, para que coincida con el server.
  const existingBySlot = useMemo(() => {
    const map = new Map<string, ServerAttachment[]>();
    for (const a of existingQuery.data?.attachments ?? []) {
      let key: string | null = null;
      if (a.itemId) key = `item:${a.itemId}`;
      else if (a.section) key = `section:${a.section}`;
      if (!key) continue;
      const arr = map.get(key) ?? [];
      arr.push({ id: a.id, filename: a.filename, mimeType: a.mimeType });
      map.set(key, arr);
    }
    return map;
  }, [existingQuery.data?.attachments]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<StandupCreateInput>({
    resolver: zodResolver(standupCreateSchema),
    defaultValues: {
      date: todayIso(),
      items: [],
      yesterdayBlockers: "",
      generalDifficulties: "",
      todayBlockers: "",
      toolingFriction: "",
      comments: "",
      mentionedUserIds: [],
      postToMattermost: true,
      crossPostToProjects: true,
      postCommentsToPlane: true,
    },
  });

  const itemsArray = useFieldArray({ control, name: "items" });

  // Prefill desde existingQuery cuando estamos en edit-mode. Solo corre
  // una vez al cargar la data (no en cada refetch).
  useEffect(() => {
    if (!isEdit || !existingQuery.data) return;
    const d = existingQuery.data;
    const ticketsByItem = new Map<string, typeof d.tickets>();
    for (const t of d.tickets) {
      const arr = ticketsByItem.get(t.itemId) ?? [];
      arr.push(t);
      ticketsByItem.set(t.itemId, arr);
    }
    const mentionsByItem = new Map<string, string[]>();
    for (const m of d.mentions) {
      const arr = mentionsByItem.get(m.itemId) ?? [];
      arr.push(m.userId);
      mentionsByItem.set(m.itemId, arr);
    }
    const items = d.items.map((it) => ({
      id: it.id,
      section: it.section,
      position: it.position,
      text: it.text ?? "",
      panelProjectId: it.panelProjectId ?? undefined,
      tickets: (ticketsByItem.get(it.id) ?? [])
        .toSorted((a, b) => a.position - b.position)
        .map((t) => ({
          planeProjectId: t.planeProjectId,
          planeIssueId: t.planeIssueId,
          issueTitleSnapshot: t.issueTitleSnapshot,
          issueUrl: t.issueUrl,
          sequenceId: t.sequenceId ?? undefined,
          projectIdentifier: t.projectIdentifier ?? undefined,
        })),
      mentionUserIds: mentionsByItem.get(it.id) ?? [],
    }));
    reset({
      date: d.standup.date,
      items,
      yesterdayBlockers: d.standup.yesterdayBlockers ?? "",
      generalDifficulties: d.standup.generalDifficulties ?? "",
      todayBlockers: d.standup.todayBlockers ?? "",
      toolingFriction: d.standup.toolingFriction ?? "",
      comments: d.standup.comments ?? "",
      mentionedUserIds: d.standup.mentionedUserIds ?? [],
      postToMattermost: false,
      crossPostToProjects: false,
      postCommentsToPlane: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingQuery.data?.standup.date]);

  // Autosave a localStorage para no perder progreso si se refresca el
  // tab. Persistimos el form (excepto archivos, que no se serializan) y
  // restauramos en mount. Borramos al publicar exitosamente.
  // En edit-mode usamos otra key para no pisar el draft del día.
  const draftKey = isEdit
    ? `axorial.standup.edit.${editingId}`
    : `axorial.standup.draft.${todayIso()}`;
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<StandupCreateInput>;
        reset({ ...parsed, date: todayIso() } as StandupCreateInput);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const sub = watch((value) => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(value));
        setDraftSavedAt(new Date());
      } catch {
        /* quota */
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  // Archivos pendientes a subir tras crear el standup. La key identifica
  // el slot:
  //   - "item:<rhf-field-id>"  → archivos pegados al item con ese id
  //   - "section:<sectionKey>" → archivos pegados a la pregunta de texto
  // El submit primero crea el standup, recibe los IDs de los items y
  // luego sube cada archivo a /api/standups/:id/attachments.
  const [pendingFiles, setPendingFiles] = useState<Record<string, File[]>>({});
  const [uploadState, setUploadState] = useState<{
    phase: "idle" | "uploading";
    done: number;
    total: number;
  }>({ phase: "idle", done: 0, total: 0 });

  const addFile = (slotKey: string, files: FileList | File[]) => {
    setPendingFiles((prev) => {
      const existing = prev[slotKey] ?? [];
      // Filtrar solo imágenes (backend rechaza el resto de todos modos).
      const accepted = Array.from(files).filter((f) => f.type.startsWith("image/"));
      return { ...prev, [slotKey]: [...existing, ...accepted] };
    });
  };
  const removeFile = (slotKey: string, idx: number) => {
    setPendingFiles((prev) => {
      const existing = prev[slotKey] ?? [];
      return { ...prev, [slotKey]: existing.filter((_, i) => i !== idx) };
    });
  };

  const onSubmit = async (input: StandupCreateInput) => {
    let created: { id: string; items: { id: string; section: string; position: number }[] };
    try {
      if (isEdit && editingId) {
        created = await api.patch(`/standups/${editingId}`, input);
      } else {
        created = await api.post("/standups", input);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const existing = (err.payload as { standupId?: string })?.standupId;
        if (existing) {
          navigate({ to: "/standups/$standupId", params: { standupId: existing } });
        }
      }
      return;
    }

    // Mapear (section, position) → serverItemId para resolver "item:<fieldId>"
    // a "itemId real".
    const serverItemBySectionPos = new Map<string, string>();
    for (const it of created.items) {
      serverItemBySectionPos.set(`${it.section}:${it.position}`, it.id);
    }

    // Lista plana de uploads pendientes con su itemId/section ya resuelto.
    type Pending = { file: File; itemId?: string; section?: string };
    const uploads: Pending[] = [];
    for (const [slotKey, files] of Object.entries(pendingFiles)) {
      if (files.length === 0) continue;
      if (slotKey.startsWith("item:")) {
        const fieldId = slotKey.slice("item:".length);
        const field = itemsArray.fields.find(
          (f) => (f as unknown as { id: string }).id === fieldId,
        );
        if (!field) continue;
        const section = (field as unknown as { section: string }).section;
        const pos = (field as unknown as { position: number }).position;
        const itemId = serverItemBySectionPos.get(`${section}:${pos}`);
        if (!itemId) continue;
        for (const file of files) uploads.push({ file, itemId });
      } else if (slotKey.startsWith("section:")) {
        const section = slotKey.slice("section:".length);
        for (const file of files) uploads.push({ file, section });
      }
    }

    if (uploads.length > 0) {
      setUploadState({ phase: "uploading", done: 0, total: uploads.length });
      // Secuencial por simplicidad — si la lista crece movemos a paralelo
      // con bound concurrency. Cualquier fallo individual lo logueamos
      // pero seguimos con el resto (los attachments faltantes se pueden
      // re-subir entrando al standup detail).
      let done = 0;
      for (const up of uploads) {
        try {
          const form = new FormData();
          form.append("file", up.file);
          if (up.itemId) form.append("itemId", up.itemId);
          if (up.section) form.append("section", up.section);
          await api.post(`/standups/${created.id}/attachments`, form);
        } catch (err) {
          console.error("standup attachment upload failed", up.file.name, err);
        }
        done++;
        setUploadState({ phase: "uploading", done, total: uploads.length });
      }
    }

    try {
      localStorage.removeItem(draftKey);
    } catch {
      /* ignore */
    }
    qc.invalidateQueries({ queryKey: ["standups"] });
    qc.invalidateQueries({ queryKey: ["standup", "me", "today"] });
    navigate({ to: "/standups/$standupId", params: { standupId: created.id } });
  };

  const availableIssues = issuesQuery.data?.issues ?? [];
  const availableProjects = useMemo(
    () => (projectsQuery.data?.projects ?? []).filter((p) => p.status !== "archived"),
    [projectsQuery.data],
  );
  const availableUsers = (usersQuery.data?.users ?? []).filter((u) => u.active !== false);

  const addItem = (section: (typeof STANDUP_ITEM_SECTION_KEYS)[number]) => {
    const sameSectionCount = itemsArray.fields.filter(
      (f) => (f as unknown as { section: string }).section === section,
    ).length;
    itemsArray.append({
      section,
      position: sameSectionCount,
      text: "",
      tickets: [],
      mentionUserIds: [],
    });
  };

  /**
   * Prellena la sección "Ayer" con los items del último día hábil del
   * usuario (toma su standup anterior en sección "today" — lo que había
   * planeado ayer ≈ lo que hizo ayer hoy). No reemplaza items ya
   * cargados; los agrega al final con position consecutiva.
   */
  const [prefillState, setPrefillState] = useState<{
    phase: "idle" | "loading" | "ok" | "error";
    message?: string;
  }>({ phase: "idle" });
  const prefillFromYesterday = async () => {
    setPrefillState({ phase: "loading" });
    try {
      const resp = await api.get<{
        date: string;
        items: Array<{
          id: string;
          text: string | null;
          panelProjectId: string | null;
          section: string;
          position: number;
        }>;
        tickets: Array<{
          id: string;
          itemId: string;
          position: number;
          planeProjectId: string;
          planeIssueId: string;
          issueTitleSnapshot: string;
          issueUrl: string;
          sequenceId: number | null;
          projectIdentifier: string | null;
        }>;
      }>("/standups/me/last-business-day");
      if (resp.items.length === 0) {
        setPrefillState({
          phase: "error",
          message: `No hay standup del ${resp.date} para prellenar`,
        });
        return;
      }
      const ticketsByItem = new Map<string, typeof resp.tickets>();
      for (const t of resp.tickets) {
        const arr = ticketsByItem.get(t.itemId) ?? [];
        arr.push(t);
        ticketsByItem.set(t.itemId, arr);
      }
      // Posición de arranque = los items que ya existen en "yesterday".
      const existingYesterdayCount = itemsArray.fields.filter(
        (f) => (f as unknown as { section: string }).section === "yesterday",
      ).length;
      for (let i = 0; i < resp.items.length; i++) {
        const src = resp.items[i]!;
        const srcTickets = (ticketsByItem.get(src.id) ?? []).toSorted(
          (a, b) => a.position - b.position,
        );
        itemsArray.append({
          section: "yesterday",
          position: existingYesterdayCount + i,
          text: src.text ?? "",
          panelProjectId: src.panelProjectId ?? undefined,
          tickets: srcTickets.map((t) => ({
            planeProjectId: t.planeProjectId,
            planeIssueId: t.planeIssueId,
            issueTitleSnapshot: t.issueTitleSnapshot,
            issueUrl: t.issueUrl,
            sequenceId: t.sequenceId ?? undefined,
            projectIdentifier: t.projectIdentifier ?? undefined,
          })),
          mentionUserIds: [],
        });
      }
      setPrefillState({
        phase: "ok",
        message: `Prellenado con ${resp.items.length} tarea${resp.items.length === 1 ? "" : "s"} del ${resp.date}`,
      });
    } catch (err) {
      setPrefillState({ phase: "error", message: String(err) });
    }
  };

  const itemsBySection = useMemo(() => {
    const map = new Map<string, { field: (typeof itemsArray.fields)[number]; index: number }[]>();
    itemsArray.fields.forEach((field, index) => {
      const section = (field as unknown as { section: string }).section;
      const arr = map.get(section) ?? [];
      arr.push({ field, index });
      map.set(section, arr);
    });
    return map;
  }, [itemsArray.fields]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={`Standup ${todayIso()}${meQuery.data ? ` · ${meQuery.data.name}` : ""}`}
        title={isEdit ? "Editar standup" : "Tu standup de hoy"}
        subtitle="Ayer y hoy se llenan tarea por tarea (proyecto + tickets + descripción). El resto es texto libre. Menciones con @ en cualquier lado."
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <input type="hidden" {...register("date")} />

        {FORM_SECTIONS.map((s, idx) => {
          const num = String(idx + 1).padStart(2, "0");
          if (s.kind === "items") {
            const sectionItems = itemsBySection.get(s.key) ?? [];
            // Borde primary para que se distinga visualmente de las
            // preguntas de texto libre (sin borde).
            return (
              <section
                key={s.key}
                className="card-elevated p-4 space-y-3 border-l-4 border-primary"
              >
                <header className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-bold tabular-nums text-primary">{num}</span>
                  <Briefcase className="size-4 text-primary shrink-0" />
                  <h3 className="flex-1 font-display font-semibold text-base leading-snug">
                    {s.label}
                  </h3>
                  {/* Botón de prefill solo aplica para "Ayer". Lo que
                      planeaste ayer (sección "today" del standup anterior)
                      se convierte en lo que hiciste hoy → tu "ayer". */}
                  {s.key === "yesterday" && (
                    <button
                      type="button"
                      onClick={prefillFromYesterday}
                      disabled={prefillState.phase === "loading"}
                      className="btn btn-ghost btn-xs gap-1.5"
                      title="Prellena con las tareas del último día hábil"
                    >
                      {prefillState.phase === "loading" ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : (
                        <History className="size-3" />
                      )}
                      Prellenar día anterior
                    </button>
                  )}
                  {sectionItems.length > 0 && (
                    <span className="badge badge-primary badge-sm">
                      {sectionItems.length} {sectionItems.length === 1 ? "tarea" : "tareas"}
                    </span>
                  )}
                </header>
                {s.key === "yesterday" && prefillState.phase !== "idle" && prefillState.message && (
                  <p
                    className={
                      "text-xs px-1 " +
                      (prefillState.phase === "error" ? "text-error" : "text-success")
                    }
                  >
                    {prefillState.message}
                  </p>
                )}

                {sectionItems.length > 0 && (
                  <ul className="space-y-3">
                    {sectionItems.map(({ field, index }) => {
                      const slotKey = `item:${(field as unknown as { id: string }).id}`;
                      return (
                        <ItemCard
                          key={index}
                          control={control}
                          setValue={setValue}
                          index={index}
                          onRemove={() => itemsArray.remove(index)}
                          projects={availableProjects}
                          issues={availableIssues}
                          issuesLoading={issuesQuery.isLoading || refreshIssues.isPending}
                          onRefreshIssues={() => refreshIssues.mutate()}
                          users={availableUsers}
                          files={pendingFiles[slotKey] ?? []}
                          onFilesAdd={(fs) => addFile(slotKey, fs)}
                          onFileRemove={(i) => removeFile(slotKey, i)}
                          existingBySlot={existingBySlot}
                          onExistingDelete={(attId) => deleteAttachment.mutate(attId)}
                        />
                      );
                    })}
                  </ul>
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => addItem(s.key)}
                    className="btn btn-primary btn-sm"
                  >
                    <Plus className="size-4" />{" "}
                    {sectionItems.length === 0 ? "Agregar primera tarea" : "Agregar tarea"}
                  </button>
                </div>
              </section>
            );
          }
          // Texto libre — MentionTextarea (mismo que items para que @
          // funcione aquí también) + file slot para adjuntar imágenes
          // a la pregunta como tal (no a un item).
          const sectionKey = (
            {
              yesterdayBlockers: "yesterday_blockers",
              generalDifficulties: "general_difficulties",
              todayBlockers: "today_blockers",
              toolingFriction: "tooling_friction",
              comments: "comments",
            } as const
          )[s.key];
          const slotKey = `section:${sectionKey}`;
          return (
            <section key={s.key} className="card-elevated p-4 space-y-3">
              <header className="flex items-start gap-3">
                <span className="text-xs font-bold tabular-nums text-primary mt-1">{num}</span>
                <h3 className="flex-1 font-display font-semibold text-base leading-snug">
                  {s.label}
                </h3>
              </header>
              {/* Texto y archivos en fila — `items-stretch` para que ambos
                  hijos crezcan al alto del más alto. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
                <Controller
                  control={control}
                  name={s.key}
                  render={({ field }) => (
                    <Controller
                      control={control}
                      name="mentionedUserIds"
                      render={({ field: mField }) => (
                        <MentionTextarea
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          mentionUserIds={(mField.value ?? []) as string[]}
                          onMentionUserIdsChange={mField.onChange}
                          users={availableUsers}
                          placeholder={s.placeholder}
                          rows={5}
                        />
                      )}
                    />
                  )}
                />
                <FileSlot
                  files={pendingFiles[slotKey] ?? []}
                  existing={existingBySlot.get(slotKey) ?? []}
                  onAdd={(fs) => addFile(slotKey, fs)}
                  onRemove={(i) => removeFile(slotKey, i)}
                  onExistingDelete={(attId) => deleteAttachment.mutate(attId)}
                />
              </div>
            </section>
          );
        })}

        {/* Resumen de menciones del standup — incluye las inline en
            free-text + las que vengan de cualquier item. Si se mencionó
            a alguien sin querer es más fácil notarlo aquí que releyendo
            todo el formulario. */}
        <MentionSummary control={control} users={availableUsers} />

        <section className="card-elevated p-4 space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/65 mb-2">
            Publicación
          </h4>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              {...register("postToMattermost")}
            />
            <span className="text-sm">Postear standup completo al canal global de Mattermost</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              {...register("crossPostToProjects")}
            />
            <span className="text-sm">
              Cross-post a canales de proyecto (un mensaje por cada tarea con proyecto asignado)
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              {...register("postCommentsToPlane")}
            />
            <span className="text-sm">Comentar en cada ticket de Plane</span>
          </label>
        </section>

        {/* Sticky bar abajo con counters + acción primaria, queda visible
            durante todo el scroll. */}
        <StickyActions
          itemCount={itemsArray.fields.length}
          fileCount={Object.values(pendingFiles).reduce((acc, fs) => acc + fs.length, 0)}
          draftSavedAt={draftSavedAt}
          submitting={isSubmitting}
          uploadState={uploadState}
          isEdit={isEdit}
          onCancel={() => navigate({ to: "/standups" })}
        />
      </form>
    </div>
  );
}

function StickyActions({
  itemCount,
  fileCount,
  draftSavedAt,
  submitting,
  uploadState,
  isEdit,
  onCancel,
}: {
  itemCount: number;
  fileCount: number;
  draftSavedAt: Date | null;
  submitting: boolean;
  uploadState: { phase: "idle" | "uploading"; done: number; total: number };
  isEdit: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="sticky bottom-0 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-3 bg-base-100/95 backdrop-blur border-t border-base-300/60 z-10">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs text-base-content/55 flex-1 min-w-0 flex items-center gap-3 flex-wrap">
          <span>
            <strong className="text-base-content tabular-nums">{itemCount}</strong> tareas
          </span>
          <span>·</span>
          <span>
            <strong className="text-base-content tabular-nums">{fileCount}</strong> archivos
          </span>
          {draftSavedAt && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1 text-success">
                <CheckCircle2 className="size-3" />
                Borrador guardado{" "}
                {draftSavedAt.toLocaleTimeString("es-MX", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            </>
          )}
        </div>
        <button type="button" onClick={onCancel} className="btn btn-ghost btn-sm">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting || uploadState.phase === "uploading"}
          className="btn btn-primary"
        >
          {(submitting || uploadState.phase === "uploading") && (
            <span className="loading loading-spinner loading-xs" />
          )}
          {uploadState.phase === "uploading"
            ? `Subiendo (${uploadState.done}/${uploadState.total})`
            : isEdit
              ? "Guardar cambios"
              : "Publicar standup"}
        </button>
      </div>
    </div>
  );
}

/** Strip de chips con los usuarios mencionados en cualquier campo. */
function MentionSummary({
  control,
  users,
}: {
  control: Control<StandupCreateInput>;
  users: User[];
}) {
  const globalIds = (useWatch({ control, name: "mentionedUserIds" }) ?? []) as string[];
  const items = (useWatch({ control, name: "items" }) ?? []) as Array<{
    mentionUserIds?: string[];
  }>;
  const all = new Set<string>(globalIds);
  for (const it of items) for (const id of it.mentionUserIds ?? []) all.add(id);
  if (all.size === 0) return null;
  const mentioned = users.filter((u) => all.has(u.id));
  return (
    <section className="card-elevated p-3 flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-base-content/65">
        <AtSign className="size-3" /> Mencionas a
      </span>
      {mentioned.map((u) => (
        <span key={u.id} className="badge badge-primary badge-sm">
          {u.name}
        </span>
      ))}
    </section>
  );
}

// ─── Item card ────────────────────────────────────────────────────────────

function ItemCard({
  control,
  setValue,
  index,
  onRemove,
  projects,
  issues,
  issuesLoading,
  onRefreshIssues,
  users,
  files,
  onFilesAdd,
  onFileRemove,
  existingBySlot,
  onExistingDelete,
}: {
  control: Control<StandupCreateInput>;
  setValue: ReturnType<typeof useForm<StandupCreateInput>>["setValue"];
  index: number;
  onRemove: () => void;
  projects: Project[];
  issues: PlaneIssueSummary[];
  issuesLoading: boolean;
  onRefreshIssues: () => void;
  users: User[];
  files: File[];
  onFilesAdd: (files: FileList | File[]) => void;
  onFileRemove: (idx: number) => void;
  /**
   * Map de attachments existentes en el server, keyed por slot. La
   * ItemCard usa el `id` de su item (form data) para buscar
   * `item:<serverId>`. Si es create-mode el item no tiene server id y
   * el lookup devuelve nada (correcto — no hay nada subido aún).
   */
  existingBySlot: Map<string, ServerAttachment[]>;
  onExistingDelete: (attId: string) => void;
}) {
  const path = `items.${index}` as const;
  const serverItemId = useWatch({ control, name: `${path}.id` }) as string | undefined;
  const existing = serverItemId ? (existingBySlot.get(`item:${serverItemId}`) ?? []) : [];
  const tickets = (useWatch({ control, name: `${path}.tickets` }) ?? []) as Array<{
    planeIssueId: string;
    issueTitleSnapshot: string;
    issueUrl: string;
    sequenceId?: number;
    projectIdentifier?: string;
    planeProjectId: string;
  }>;

  const addTicket = (issue: PlaneIssueSummary) => {
    if (!issue.projectId) return;
    // Evitar duplicados (mismo planeIssueId).
    if (tickets.some((t) => t.planeIssueId === issue.id)) return;
    const next = [
      ...tickets,
      {
        planeProjectId: issue.projectId,
        planeIssueId: issue.id,
        issueTitleSnapshot: issue.name,
        issueUrl: issue.url,
        sequenceId: issue.sequenceId,
        projectIdentifier: issue.projectIdentifier,
      },
    ];
    setValue(`${path}.tickets`, next, { shouldDirty: true });
    // Auto-set del proyecto del panel si está mapeado y aún no hay uno.
    if (issue.panelProjectId) {
      setValue(`${path}.panelProjectId`, issue.panelProjectId, { shouldDirty: true });
    }
  };
  const removeTicket = (planeIssueId: string) => {
    setValue(
      `${path}.tickets`,
      tickets.filter((t) => t.planeIssueId !== planeIssueId),
      { shouldDirty: true },
    );
  };

  return (
    <li className="rounded-lg border border-base-300/60 p-3 space-y-2 bg-base-200/30">
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_2fr] gap-2">
        {/* Col 1: Tickets — lista + combobox para agregar */}
        <div className="form-control w-full">
          <span className="text-xs text-base-content/55 mb-0.5 flex items-center gap-1">
            <TicketIcon className="size-3" /> Tickets
            {tickets.length > 1 && (
              <span className="badge badge-ghost badge-xs">{tickets.length}</span>
            )}
          </span>
          {tickets.length > 0 && (
            <ul className="space-y-1 mb-1.5">
              {tickets.map((t) => {
                const num =
                  t.projectIdentifier && t.sequenceId !== undefined
                    ? `${t.projectIdentifier}-${t.sequenceId}`
                    : null;
                return (
                  <li
                    key={t.planeIssueId}
                    className="text-xs px-2 py-1 rounded border border-primary/40 bg-primary/5 flex items-center gap-1.5"
                  >
                    {num && (
                      <span className="font-mono text-[10px] text-base-content/55 shrink-0">
                        {num}
                      </span>
                    )}
                    <span className="truncate flex-1">{t.issueTitleSnapshot}</span>
                    <button
                      type="button"
                      onClick={() => removeTicket(t.planeIssueId)}
                      className="text-base-content/40 hover:text-error shrink-0"
                      aria-label="Quitar ticket"
                      title="Quitar ticket"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <TicketCombobox
            placeholder={
              tickets.length === 0 ? "Buscar ticket (ej. KIPLY-15)" : "Agregar otro ticket…"
            }
            issues={issues}
            issuesLoading={issuesLoading}
            onRefresh={onRefreshIssues}
            excludeIds={tickets.map((t) => t.planeIssueId)}
            onPick={addTicket}
          />
        </div>

        {/* Col 2: Proyecto — controlled para que el auto-set al elegir
            ticket se refleje visualmente. Con register() el select queda
            uncontrolled y setValue no actualiza el DOM. */}
        <label className="form-control w-full">
          <span className="text-xs text-base-content/55 mb-0.5 flex items-center gap-1">
            <Briefcase className="size-3" /> Proyecto
          </span>
          <Controller
            control={control}
            name={`${path}.panelProjectId`}
            render={({ field }) => (
              <select
                className="select select-bordered select-sm w-full"
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value === "" ? undefined : e.target.value)}
              >
                <option value="">— sin proyecto —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          />
        </label>

        {/* Col 3: Descripción con @ inline */}
        <div className="form-control w-full">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs text-base-content/55">
              Descripción · <span className="text-base-content/40">@ para mencionar</span>
            </span>
            <button
              type="button"
              onClick={onRemove}
              className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-error -mr-1 -mt-1"
              aria-label="Eliminar tarea"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
          <Controller
            control={control}
            name={`${path}.text`}
            render={({ field: textField }) => (
              <Controller
                control={control}
                name={`${path}.mentionUserIds`}
                render={({ field: mentionField }) => (
                  <MentionTextarea
                    value={textField.value ?? ""}
                    onChange={textField.onChange}
                    mentionUserIds={(mentionField.value ?? []) as string[]}
                    onMentionUserIdsChange={mentionField.onChange}
                    users={users}
                  />
                )}
              />
            )}
          />
        </div>
      </div>

      <FileSlot
        files={files}
        existing={existing}
        onAdd={onFilesAdd}
        onRemove={onFileRemove}
        onExistingDelete={onExistingDelete}
      />
    </li>
  );
}

// ─── File slot ────────────────────────────────────────────────────────────

/**
 * Dropzone + grid de previews para imágenes pegadas a un slot (item o
 * sección). Mantiene los archivos en memoria; el componente padre los
 * acumula y los sube tras crear el standup. Soporta:
 *   - Click → file picker
 *   - Drag-and-drop sobre el dropzone
 *   - Paste de imagen (cuando el dropzone tiene foco)
 */
interface ServerAttachment {
  id: string;
  filename: string;
  mimeType: string;
}

function FileSlot({
  files,
  existing = [],
  onAdd,
  onRemove,
  onExistingDelete,
}: {
  files: File[];
  existing?: ServerAttachment[];
  onAdd: (files: FileList | File[]) => void;
  onRemove: (idx: number) => void;
  onExistingDelete?: (attId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // En modo compacto si NO hay archivos (ni pendientes ni ya subidos).
  // Cuando hay attachments existentes (edit), siempre se renderiza
  // expandido para que el usuario los vea.
  const [expanded, setExpanded] = useState(false);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => {
    return () => previews.forEach((u) => URL.revokeObjectURL(u));
  }, [previews]);
  const showDropzone = expanded || files.length > 0 || existing.length > 0;

  // Drop handler global a nivel del contenedor — funciona aún cuando el
  // dropzone visible no está abierto. Así el usuario puede simplemente
  // arrastrar imagen y se agregan sin extra clicks.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      setExpanded(true);
      onAdd(e.dataTransfer.files);
    }
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const imgs: File[] = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) imgs.push(f);
      }
    }
    if (imgs.length > 0) {
      setExpanded(true);
      onAdd(imgs);
    }
  };

  if (!showDropzone) {
    return (
      <button
        type="button"
        onClick={() => {
          setExpanded(true);
          requestAnimationFrame(() => inputRef.current?.click());
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={
          "rounded-md px-3 py-1.5 text-xs text-base-content/55 hover:text-primary transition-colors flex items-center gap-1.5 self-start " +
          (dragOver ? "bg-primary/10 text-primary" : "")
        }
      >
        <Paperclip className="size-3" />
        Adjuntar imágenes
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) onAdd(e.target.files);
            e.target.value = "";
          }}
        />
      </button>
    );
  }

  return (
    <div
      className="rounded-md border border-base-300/40 bg-base-200/20 p-3 space-y-2"
      onPaste={handlePaste}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-base-content/60">
        <Paperclip className="size-3" />
        Archivos adjuntos
        {files.length + existing.length > 0 && (
          <span className="badge badge-ghost badge-xs">{files.length + existing.length}</span>
        )}
      </div>
      {(existing.length > 0 || files.length > 0) && (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {/* Existentes (ya subidos al server). Tienen × para borrar
              definitivamente vía DELETE /api/standups/attachments/:id. */}
          {existing.map((a) => (
            <li
              key={a.id}
              className="relative rounded border border-primary/30 overflow-hidden bg-base-100"
            >
              {a.mimeType.startsWith("image/") ? (
                <img
                  src={`/api/standups/attachments/${a.id}`}
                  alt={a.filename}
                  className="w-full aspect-video object-cover"
                />
              ) : (
                <div className="aspect-video grid place-items-center text-xs text-base-content/55 p-2">
                  {a.filename}
                </div>
              )}
              {onExistingDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `¿Borrar "${a.filename}"? Se elimina del standup permanentemente.`,
                      )
                    ) {
                      onExistingDelete(a.id);
                    }
                  }}
                  className="absolute top-1 right-1 btn btn-circle btn-xs bg-base-100/90 text-error border-0"
                  aria-label="Borrar imagen"
                  title="Borrar imagen del server"
                >
                  <X className="size-3" />
                </button>
              )}
              <div className="px-1.5 py-0.5 text-[10px] truncate text-base-content/55">
                {a.filename}
              </div>
            </li>
          ))}
          {/* Pendientes (todavía no subidos — vendrán en el próximo save). */}
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="relative rounded border border-base-300/60 overflow-hidden bg-base-100"
            >
              <img src={previews[i]} alt={f.name} className="w-full aspect-video object-cover" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 btn btn-circle btn-xs bg-base-100/90 text-error border-0"
                aria-label="Quitar imagen"
                title="Quitar imagen"
              >
                <X className="size-3" />
              </button>
              <div className="px-1.5 py-0.5 text-[10px] truncate text-base-content/55">
                {f.name}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={
          "border-2 border-dashed rounded-md p-3 text-center text-xs cursor-pointer transition-colors " +
          (dragOver
            ? "border-primary bg-primary/10 text-primary"
            : "border-base-300/60 text-base-content/55 hover:border-primary/50 hover:text-base-content/75")
        }
      >
        Arrastra, pega o haz click para agregar más
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onAdd(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// ─── Ticket combobox ──────────────────────────────────────────────────────

/**
 * Input + dropdown buscable para AGREGAR tickets a la lista del item.
 * Filtra por título, identifier del proyecto, sequence id y combinaciones
 * tipo "KIPLY-15" o "KIPLY 15". `excludeIds` oculta los ya elegidos.
 * Soporta navegación con teclado: flechas, Enter, Esc.
 */
function TicketCombobox({
  issues,
  issuesLoading = false,
  onRefresh,
  onPick,
  placeholder,
  excludeIds = [],
}: {
  issues: PlaneIssueSummary[];
  /** Si true, mostramos spinner en el dropdown en lugar de "Sin resultados".
   * Antes el dropdown decía "Sin resultados" mientras /me/issues seguía
   * cargando, lo que hacía pensar que el buscador estaba roto en la
   * primera interacción del día (cuando el cache de Plane estaba frío). */
  issuesLoading?: boolean;
  /** Click en el botón refresh: invalida cache de Plane y vuelve a
   * pedir /me/issues. Útil si el usuario acaba de crear/asignarse un
   * ticket en Plane y no aparece en la lista local. */
  onRefresh?: () => void;
  onPick: (issue: PlaneIssueSummary) => void;
  placeholder?: string;
  excludeIds?: string[];
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<number | null>(null);

  const filtered = useMemo(() => {
    const excluded = new Set(excludeIds);
    const q = query.trim().toLowerCase();
    const pool = issues.filter((i) => !excluded.has(i.id));
    if (!q) return pool.slice(0, 50);
    return pool
      .filter((i) => {
        const id =
          i.projectIdentifier && i.sequenceId !== undefined
            ? `${i.projectIdentifier}-${i.sequenceId}`.toLowerCase()
            : "";
        return (
          i.name.toLowerCase().includes(q) ||
          (i.projectIdentifier ?? "").toLowerCase().includes(q) ||
          id.includes(q) ||
          (i.sequenceId !== undefined && String(i.sequenceId).includes(q))
        );
      })
      .slice(0, 50);
  }, [query, issues, excludeIds]);

  function pick(i: PlaneIssueSummary) {
    onPick(i);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const i = filtered[highlight];
      if (i) pick(i);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="relative flex items-stretch gap-1.5">
      <div className="relative flex-1 min-w-0">
        <input
          ref={inputRef}
          type="text"
          className="input input-bordered input-sm w-full pr-8"
          placeholder={
            issuesLoading
              ? "Cargando tus tickets de Plane…"
              : (placeholder ?? "Buscar por título o número (ej. KIPLY-15)")
          }
          value={query}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimerRef.current = window.setTimeout(() => setOpen(false), 150);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
        />
        {issuesLoading && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <span className="loading loading-spinner loading-xs text-base-content/55" />
          </span>
        )}
        {open && filtered.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg">
            {filtered.map((i, idx) => {
              const isHighlighted = idx === highlight;
              const num =
                i.projectIdentifier && i.sequenceId !== undefined
                  ? `${i.projectIdentifier}-${i.sequenceId}`
                  : (i.projectIdentifier ?? "");
              return (
                <button
                  key={i.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (blurTimerRef.current) {
                      clearTimeout(blurTimerRef.current);
                      blurTimerRef.current = null;
                    }
                    pick(i);
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                  className={
                    "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 " +
                    (isHighlighted ? "bg-primary text-primary-content" : "hover:bg-base-200")
                  }
                >
                  {num && (
                    <span
                      className={
                        "text-xs font-mono shrink-0 " +
                        (isHighlighted ? "opacity-80" : "text-base-content/55")
                      }
                    >
                      {num}
                    </span>
                  )}
                  <span className="truncate">{i.name}</span>
                </button>
              );
            })}
          </div>
        )}
        {open && filtered.length === 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-base-300 bg-base-100 shadow-lg">
            {issuesLoading ? (
              <div className="px-3 py-2.5 text-xs text-base-content/70 inline-flex items-center gap-2">
                <span className="loading loading-spinner loading-xs" />
                Cargando tus tickets de Plane…
              </div>
            ) : (
              <RemoteIssueResolver
                query={query}
                excludeIds={excludeIds}
                onPick={(i) => {
                  if (blurTimerRef.current) {
                    clearTimeout(blurTimerRef.current);
                    blurTimerRef.current = null;
                  }
                  pick(i);
                }}
              />
            )}
          </div>
        )}
      </div>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={issuesLoading}
          className="btn btn-ghost btn-sm btn-square shrink-0"
          title="Recargar tickets desde Plane (por si no ves alguno reciente)"
          aria-label="Recargar tickets"
        >
          <RefreshCw className={"size-3.5 " + (issuesLoading ? "animate-spin" : "")} />
        </button>
      )}
    </div>
  );
}

/**
 * Cuando el filtro local no encuentra nada, ofrecemos buscar el ticket
 * directo en Plane vía /standups/issues/resolve — útil si el ticket no
 * está asignado al user (no aparece en /me/issues). Soporta refs tipo
 * "KIPLY-131" o pegar la URL completa del ticket.
 */
function RemoteIssueResolver({
  query,
  excludeIds,
  onPick,
}: {
  query: string;
  excludeIds: string[];
  onPick: (i: PlaneIssueSummary) => void;
}) {
  const trimmed = query.trim();
  const looksLikeRef =
    /^[A-Za-z][A-Za-z0-9]*[\s\-_]+\d+$/.test(trimmed) || /^https?:\/\//.test(trimmed);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!trimmed) {
    return <div className="px-3 py-2 text-xs text-base-content/55">Sin resultados</div>;
  }
  if (!looksLikeRef) {
    return (
      <div className="px-3 py-2 text-xs text-base-content/55">
        Sin resultados.{" "}
        <span className="block mt-0.5 text-base-content/45">
          Tip: pegá el ID exacto (KIPLY-131) o la URL del ticket para traerlo aunque no esté
          asignado a vos.
        </span>
      </div>
    );
  }

  async function resolve() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ issue: PlaneIssueSummary }>(
        `/standups/issues/resolve?ref=${encodeURIComponent(trimmed)}`,
      );
      if (excludeIds.includes(res.issue.id)) {
        setError("Ese ticket ya está agregado.");
        return;
      }
      onPick(res.issue);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      setError(detail.includes("404") ? "Ticket no encontrado en Plane." : detail);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="px-1 py-1">
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          resolve();
        }}
        disabled={loading}
        className="w-full text-left px-3 py-2 text-sm rounded hover:bg-base-200 flex items-center gap-2"
      >
        {loading ? (
          <span className="loading loading-spinner loading-xs" />
        ) : (
          <span className="text-xs text-primary font-semibold">＋</span>
        )}
        <span className="truncate">
          Buscar <span className="font-mono">{trimmed}</span> en Plane
        </span>
      </button>
      {error && <div className="px-3 pb-2 text-xs text-error">{error}</div>}
    </div>
  );
}

// ─── @-mention textarea ────────────────────────────────────────────────────

/**
 * Textarea con autocomplete inline de menciones. Al escribir `@` después
 * de un espacio (o al inicio), aparece un dropdown filtrable de usuarios.
 * Al seleccionar uno se inserta `@FirstName` en el texto y se agrega su
 * id a `mentionUserIds` para que el backend pueda registrar la mención.
 *
 * Caveats deliberados:
 * - Si el usuario borra a mano un `@FirstName` del texto, el id queda
 *   en `mentionUserIds` (orphan). No es problema funcional — la mención
 *   simplemente no se ve en el post pero queda registrada. Mantenerlo
 *   sin sincronizar evita lógica frágil de matching por nombre.
 * - Soportamos primer nombre solo. Si dos personas se llaman "Jesús"
 *   ambos quedan registrados con `@Jesús` — la UI mostraría el nombre
 *   completo en el dropdown para distinguirlos.
 */
function MentionTextarea({
  value,
  onChange,
  users,
  mentionUserIds,
  onMentionUserIdsChange,
  placeholder = "Describe la tarea. Usa @ para mencionar compañeros.",
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  users: User[];
  mentionUserIds: string[];
  onMentionUserIdsChange: (next: string[]) => void;
  placeholder?: string;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [highlight, setHighlight] = useState(0);

  // Detecta si la posición del caret está dentro de un `@word` que empezó
  // después de un whitespace o al inicio del texto. Si sí, levanta el
  // popup con el query (lo que va entre `@` y el caret).
  function detectMention(text: string, caret: number) {
    let i = caret - 1;
    while (i >= 0 && /\w/.test(text[i] ?? "")) i--;
    if (i >= 0 && text[i] === "@") {
      const prev = i === 0 ? undefined : text[i - 1];
      if (i === 0 || (prev !== undefined && /\s/.test(prev))) {
        setMention({ start: i, query: text.slice(i + 1, caret) });
        setHighlight(0);
        return;
      }
    }
    setMention(null);
  }

  const filtered = useMemo(() => {
    if (!mention) return [] as User[];
    const q = mention.query.toLowerCase();
    return users
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [mention, users]);

  function pickUser(user: User) {
    const textarea = ref.current;
    if (!mention || !textarea) return;
    const caret = textarea.selectionStart;
    const firstName = user.name.split(" ")[0] ?? user.name;
    const before = value.slice(0, mention.start);
    const after = value.slice(caret);
    const inserted = `@${firstName} `;
    const next = before + inserted + after;
    onChange(next);
    if (!mentionUserIds.includes(user.id)) {
      onMentionUserIdsChange([...mentionUserIds, user.id]);
    }
    setMention(null);
    requestAnimationFrame(() => {
      const pos = before.length + inserted.length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  }

  // Si el popup está abierto, capturamos teclas: arriba/abajo navegan,
  // Enter elige el highlight, Escape cierra. Si no está abierto, el
  // textarea se comporta normal (incluido Enter para nueva línea).
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!mention || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const u = filtered[highlight];
      if (u) pickUser(u);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
    }
  }

  useEffect(() => {
    // Si el valor cambia desde afuera (form reset) y el caret no
    // coincide, cierra el popup para evitar estados raros.
    setMention(null);
  }, [value === ""]);

  return (
    <div className="relative">
      <textarea
        ref={ref}
        rows={rows}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          detectMention(e.target.value, e.target.selectionStart);
        }}
        onSelect={(e) => {
          const t = e.currentTarget;
          detectMention(t.value, t.selectionStart);
        }}
        onKeyDown={onKeyDown}
        className="textarea textarea-bordered w-full"
        placeholder={placeholder}
      />
      {mention && filtered.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg">
          {filtered.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown en vez de click para evitar perder foco antes
                // de leer selectionStart.
                e.preventDefault();
                pickUser(u);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={
                "w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 " +
                (i === highlight ? "bg-primary text-primary-content" : "hover:bg-base-200")
              }
            >
              <span className="truncate">{u.name}</span>
              <span
                className={"text-xs " + (i === highlight ? "opacity-80" : "text-base-content/50")}
              >
                {u.email}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
